import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { Switch } from '@/components/forms/Switch';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { IF_SWAPS_OPTIONS, filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { todayDateString } from '@/api/services/riskDashboard';
import type { RiskDashboardFilters } from '@/api/types/riskDashboard';

interface Props {
  draft: RiskDashboardFilters;
  setDraft: React.Dispatch<React.SetStateAction<RiskDashboardFilters>>;
  onFetch: () => void;
  onClear: () => void;
  onModeToggle: (key: keyof RiskDashboardFilters, value: boolean) => void;
  canAutoMap: boolean;
  canByUniqueId: boolean;
  statusSlot?: React.ReactNode;
}

function useOptions() {
  const q = (key: string, fn: () => Promise<{ value: string | number; label: string }[]>) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useQuery({ queryKey: ['rd-filter', key], queryFn: fn, staleTime: 5 * 60_000 });
  // Small lists loaded eagerly; the big ones (groups/instruments/countries) are
  // loaded page-by-page via Select2Async (filterDataPaged).
  return {
    connectors: q('connectors', filterDataApi.getEnabledConnectors),
    groupProfiles: q('groupProfiles', filterDataApi.getGroupProfiles),
    portfolios: q('portfolios', filterDataApi.getPortfolios),
    mifid: q('mifid', filterDataApi.getMifidClassifications),
    venues: q('venues', filterDataApi.getExecutionVenues),
  };
}

export function RiskDashboardFilterBar({ draft, setDraft, onFetch, onClear, onModeToggle, canAutoMap, canByUniqueId, statusSlot }: Props) {
  const [showAll, setShowAll] = useState(false);
  const opt = useOptions();

  const setStr = (k: keyof RiskDashboardFilters) => (v: string | null) =>
    setDraft((f) => ({ ...f, [k]: v || null }));
  const setNum = (k: keyof RiskDashboardFilters) => (v: string | null) =>
    setDraft((f) => ({ ...f, [k]: v ? Number(v) : null }));
  const str = (v: string | number | null) => (v == null ? '' : String(v));
  const clearKey = (k: keyof RiskDashboardFilters) => setDraft((f) => ({ ...f, [k]: null }));

  // Active extra-filter chips (mirrors renderActiveFilterChips). Labels resolve
  // from the loaded option lists where the draft only holds the id.
  const labelOf = (list: { value: string | number; label: string }[] | undefined, v: string | number | null) =>
    list?.find((o) => String(o.value) === String(v))?.label ?? str(v);
  const chips: { key: keyof RiskDashboardFilters; text: string }[] = [
    draft.groupName ? { key: 'groupName', text: `Group: ${draft.groupName}` } : null,
    draft.groupTextFilter ? { key: 'groupTextFilter', text: `Group Text: ${draft.groupTextFilter}` } : null,
    draft.groupProfile ? { key: 'groupProfile', text: `Profile: ${labelOf(opt.groupProfiles.data, draft.groupProfile)}` } : null,
    draft.portfolioId ? { key: 'portfolioId', text: `Portfolio: ${labelOf(opt.portfolios.data, draft.portfolioId)}` } : null,
    draft.login ? { key: 'login', text: `Login: ${draft.login}` } : null,
    draft.uniqueId ? { key: 'uniqueId', text: `UID: ${draft.uniqueId}` } : null,
    draft.instrument ? { key: 'instrument', text: `Instrument: ${draft.instrument}` } : null,
    draft.mifidFilter ? { key: 'mifidFilter', text: `MiFID: ${labelOf(opt.mifid.data, draft.mifidFilter)}` } : null,
    draft.executionVenue ? { key: 'executionVenue', text: `Venue: ${labelOf(opt.venues.data, draft.executionVenue)}` } : null,
    draft.countryId ? { key: 'countryId', text: `Country: ${draft.countryId}` } : null,
    draft.ifSwaps ? { key: 'ifSwaps', text: `If Swaps: ${draft.ifSwaps}` } : null,
  ].filter((c): c is { key: keyof RiskDashboardFilters; text: string } => c != null);

  const today = todayDateString();
  // Portfolio exclusivity: a selected portfolio greys + disables the other
  // scoping filters (mirrors risk-dashboard-index.js pe-none opacity-50).
  const lock = draft.portfolioId != null ? 'pe-none opacity-50' : '';

  return (
    <div className="card custom-card mb-2 rd-filters-card">
      <div className="card-body py-2 px-2">
        <div className="d-flex flex-wrap align-items-center gap-2 rd-filter-chrome-body">
          {/* Left: Date Range + Connector */}
          <div style={{ minWidth: 230 }}>
            <label className="form-label small mb-1 text-uppercase">Date Range</label>
            <DateRangePicker
              mode="range"
             
              maxDate={today}
              value={{ start: draft.startDate, end: draft.endDate }}
              onChange={(v) => {
                // Live↔date sync (FinkitLiveDateSync): clamp start≤end and keep Live
                // on only while the range still ends today.
                const end = v.end;
                const start = v.start > end ? end : v.start;
                setDraft((f) => ({ ...f, startDate: start, endDate: end, liveMode: end === today }));
              }}
            />
          </div>
          <div style={{ minWidth: 160 }}>
            <Select2
              label="Connector"
             
              allowClear
              placeholder="All"
              value={str(draft.platformId)}
              options={opt.connectors.data ?? []}
              onChange={setNum('platformId')}
            />
          </div>

          {/* Right: mode buttons + actions + status */}
          <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
            <div className="rd-mode-rail">
              <Switch label="Live" checked={draft.liveMode} onChange={(c) => onModeToggle('liveMode', c)} />
              <Switch label="Show LP" checked={draft.showLp} onChange={(c) => onModeToggle('showLp', c)} />
              {canAutoMap && (
                <Switch label="AutoMap" checked={draft.symbolMapping} onChange={(c) => onModeToggle('symbolMapping', c)} />
              )}
              {canByUniqueId && (
                <Switch label="UID" checked={draft.byUniqueId} onChange={(c) => onModeToggle('byUniqueId', c)} />
              )}
            </div>
            <button type="button" className="btn btn-primary btn-sm" onClick={onFetch}>
              <i className="fa fa-search me-1" />
              Fetch
            </button>
            <button
              type="button"
              className={`btn btn-sm rd-all-filters-btn ${showAll ? 'on' : ''} btn-outline-primary`}
              onClick={() => setShowAll((v) => !v)}
            >
              <i className="fe fe-sliders me-1" />
              All Filters
              {chips.length > 0 && <span className="badge bg-primary ms-1 rd-filter-count">{chips.length}</span>}
            </button>
            <button type="button" className="btn btn-link btn-sm" onClick={onClear}>
              Clear All Filters
            </button>
            {statusSlot}
          </div>
        </div>

        {chips.length > 0 && (
          <div className="rd-active-filter-chips d-flex flex-wrap align-items-center gap-1 pt-2">
            <span className="rd-chips-label fs-11 text-muted text-uppercase fw-semibold me-1">Active filters</span>
            {chips.map((c) => (
              <button key={c.key} type="button" className="rd-scope-chip btn btn-sm" onClick={() => clearKey(c.key)}>
                {c.text} <span aria-hidden>×</span>
              </button>
            ))}
          </div>
        )}

        {showAll && (
          <div className="row g-2 pt-2 mt-1 border-top">
            <AsyncSelectCol label="Group" colClassName={lock} value={str(draft.groupName)} fetch={filterDataPaged.groups} onChange={setStr('groupName')} />
            <div className={`col-md-3 ${lock}`}>
              <label className="form-label small mb-1">Group Text</label>
              <input
                className="form-control form-control-sm"
                placeholder="S*, *abc*, !SPOT"
                value={draft.groupTextFilter ?? ''}
                onChange={(e) => setStr('groupTextFilter')(e.target.value)}
              />
            </div>
            <SelectCol label="Group Profile" colClassName={lock} value={str(draft.groupProfile)} options={opt.groupProfiles.data ?? []} onChange={setStr('groupProfile')} />
            <SelectCol label="Portfolio Profile" value={str(draft.portfolioId)} options={opt.portfolios.data ?? []} onChange={setNum('portfolioId')} />

            <div className={`col-md-3 ${lock}`}>
              <label className="form-label small mb-1">Login</label>
              <input
                className="form-control form-control-sm"
                inputMode="numeric"
                value={draft.login ?? ''}
                onChange={(e) => setDraft((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))}
              />
            </div>
            <div className={`col-md-3 ${lock}`}>
              <label className="form-label small mb-1">Unique ID</label>
              <input
                className="form-control form-control-sm"
                value={draft.uniqueId ?? ''}
                onChange={(e) => setStr('uniqueId')(e.target.value)}
              />
            </div>
            <AsyncSelectCol label="Instrument" colClassName={lock} value={str(draft.instrument)} fetch={filterDataPaged.instruments} onChange={setStr('instrument')} />
            <SelectCol label="MiFID" colClassName={lock} value={str(draft.mifidFilter)} options={opt.mifid.data ?? []} onChange={setStr('mifidFilter')} />
            <SelectCol label="Execution Venue" colClassName={lock} value={str(draft.executionVenue)} options={opt.venues.data ?? []} onChange={setStr('executionVenue')} />
            <AsyncSelectCol label="Country" colClassName={lock} value={str(draft.countryId)} fetch={filterDataPaged.countries} onChange={setNum('countryId')} />
            <SelectCol label="If Swaps" colClassName={lock} value={str(draft.ifSwaps)} options={IF_SWAPS_OPTIONS} onChange={setStr('ifSwaps')} />
          </div>
        )}
      </div>
    </div>
  );
}

function SelectCol({
  label,
  value,
  options,
  onChange,
  colClassName = '',
}: {
  label: string;
  value: string;
  options: { value: string | number; label: string }[];
  onChange: (v: string | null) => void;
  colClassName?: string;
}) {
  return (
    <div className={`col-md-3 ${colClassName}`}>
      <Select2 label={label} allowClear placeholder="All" value={value} options={options} onChange={onChange} />
    </div>
  );
}

// Paginated (infinite-scroll) + searchable filter dropdown for large lists.
function AsyncSelectCol({
  label,
  value,
  fetch,
  onChange,
  colClassName = '',
}: {
  label: string;
  value: string;
  fetch: (term: string, page: number) => Promise<import('@/components/forms/Select2Async').Select2Page>;
  onChange: (v: string | null) => void;
  colClassName?: string;
}) {
  return (
    <div className={`col-md-3 ${colClassName}`}>
      <label className="form-label small mb-1">{label}</label>
      <Select2Async placeholder="All" value={value || null} fetch={fetch} onChange={onChange} />
    </div>
  );
}
